import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";
import {
  Search,
  Bell,
  User,
  Eye,
  EyeOff,
  ChevronRight,
  LogOut,
  Lock,
  IdCard,
  Calendar,
  UserCircle,
  RefreshCw,
  X,
  Trash2,
} from "lucide-react";
import useProfile from "../hooks/useProfile";
import useAuth from "../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { authFetch } from "../lib/api";
import Icons from "../components/icons";

// ===================== NOTIFICATION STORAGE HELPERS =====================

const getNotifKey = (userId) => `qwickhub_notifications_${userId}`;
const getDeletedNotifKey = (userId) =>
  `qwickhub_notifications_deleted_${userId}`;
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

const getLoggedInUserId = () => {
  try {
    return JSON.parse(localStorage.getItem("user"))?._id || "guest";
  } catch {
    return "guest";
  }
};

const loadNotifs = (userId) => {
  try {
    return JSON.parse(localStorage.getItem(getNotifKey(userId))) || [];
  } catch {
    return [];
  }
};

const loadDeletedNotifIds = (userId) => {
  try {
    const ids = JSON.parse(localStorage.getItem(getDeletedNotifKey(userId)));
    return new Set(Array.isArray(ids) ? ids : []);
  } catch {
    return new Set();
  }
};

const saveDeletedNotifIds = (userId, idSet) => {
  localStorage.setItem(
    getDeletedNotifKey(userId),
    JSON.stringify([...idSet]),
  );
};

const rememberDeletedNotifIds = (ids = []) => {
  const userId = getLoggedInUserId();
  const deleted = loadDeletedNotifIds(userId);
  ids.forEach((id) => {
    if (id && !String(id).startsWith("local_")) deleted.add(String(id));
  });
  // Cap growth — keep the most recent ~500 ids
  const trimmed = [...deleted].slice(-500);
  saveDeletedNotifIds(userId, new Set(trimmed));
};

const isBackendNotifId = (id) =>
  Boolean(id) && !String(id).startsWith("local_");

// Move notifications older than 1 week to archive automatically
const processNotifs = (notifs) => {
  const now = Date.now();
  return notifs.map((n) =>
    !n.archived && now - n.createdAt > WEEK_MS ? { ...n, archived: true } : n,
  );
};

const saveNotifs = (notifs) => {
  // Scope writes to the logged-in user — getNotifKey() with no arg wrote to
  // a shared "…_undefined" key, bleeding notifications across accounts.
  localStorage.setItem(
    getNotifKey(getLoggedInUserId()),
    JSON.stringify(notifs),
  );
};

const timeAgo = (ts) => {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  const weeks = Math.floor(diff / (7 * 86400000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} mins ago`;
  if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  if (days < 7) return `${days} day${days > 1 ? "s" : ""} ago`;
  return `${weeks} week${weeks > 1 ? "s" : ""} ago`;
};

// ===================== PASSWORD FIELD SUB-COMPONENT =====================

const PwField = ({
  label,
  value,
  show,
  onToggle,
  placeholder,
  onChange,
  hint,
  mb,
}) => (
  <div style={{ marginBottom: mb || 0 }}>
    <label
      style={{
        display: "block",
        fontSize: 14,
        fontWeight: 600,
        marginBottom: 6,
        color: "#1a1a2e",
      }}
    >
      {label}
    </label>
    <div style={{ position: "relative" }}>
      <input
        type={show ? "text" : "password"}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: "100%",
          border: "1px solid #e0e0e0",
          borderRadius: 8,
          padding: "10px 40px 10px 14px",
          fontSize: 14,
          outline: "none",
          boxSizing: "border-box",
        }}
      />
      <button
        type="button"
        onClick={onToggle}
        style={{
          position: "absolute",
          right: 12,
          top: "50%",
          transform: "translateY(-50%)",
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "#aaa",
          display: "flex",
          alignItems: "center",
        }}
      >
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
    {hint && (
      <p style={{ fontSize: 11, color: "#aaa", margin: "5px 0 0" }}>{hint}</p>
    )}
  </div>
);

// ===================== PROFILE INFO ROW =====================

const InfoRow = ({ icon, label, value, uppercase }) => (
  <div
    style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}
  >
    <div
      style={{
        width: 34,
        height: 34,
        borderRadius: 8,
        background: "#f0f4ff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {icon}
    </div>
    <div>
      <p
        style={{
          fontSize: 10,
          color: "#aaa",
          margin: 0,
          textTransform: "uppercase",
          letterSpacing: 0.5,
        }}
      >
        {label}
      </p>
      <p
        style={{
          fontSize: 13,
          fontWeight: 600,
          margin: 0,
          color: "#1a1a2e",
          textTransform: uppercase ? "uppercase" : "none",
        }}
      >
        {value}
      </p>
    </div>
  </div>
);

// ===================== NOTIFICATION ITEM =====================

const NotifItem = ({ notif, onDismiss }) => (
  <div
    style={{
      display: "flex",
      alignItems: "flex-start",
      gap: 12,
      padding: "12px 20px",
      borderBottom: "1px solid #f5f5f5",
    }}
  >
    <div
      style={{
        width: 34,
        height: 34,
        borderRadius: 8,
        background: "#F6F8FC",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        marginTop: 2,
      }}
    >
      <Icons.Envelope />
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <p
        style={{
          margin: "0 0 2px",
          fontSize: 13,
          fontWeight: 600,
          color: "#1a1a2e",
          lineHeight: 1.4,
        }}
      >
        {notif.title}
      </p>
      {(notif.lines || []).map((line, i) => (
        <p
          key={i}
          style={{ margin: 0, fontSize: 12, color: "#888", lineHeight: 1.5 }}
        >
          {line}
        </p>
      ))}
      <p style={{ margin: "4px 0 0", fontSize: 11, color: "#bbb" }}>
        {timeAgo(notif.createdAt)}
      </p>
    </div>
    <button
      onClick={(e) => {
        e.stopPropagation();
        onDismiss();
      }}
      style={{
        background: "none",
        border: "none",
        cursor: "pointer",
        color: "#ccc",
        padding: 2,
        flexShrink: 0,
        marginTop: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <X size={14} />
    </button>
  </div>
);

// ===================== MAIN TOPBAR =====================

const Topbar = ({
  onSearch = () => {},
  onNotification = () => {},
  toggleSidebar,
}) => {
  // ---- Profile ----
  const [showProfile, setShowProfile] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");

  // ---- Notifications ----
  const [showNotif, setShowNotif] = useState(false);
  const [notifTab, setNotifTab] = useState("all");
  const [notifications, setNotifications] = useState([]);
  const [visibleCount, setVisibleCount] = useState(5);

  // ---- Global project search ----
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchRef = useRef(null);
  const projects = useSelector((s) => s.dashboard.projects || []);
  const matchedProjects =
    searchQuery.trim().length > 0
      ? projects.filter((p) =>
          p.name?.toLowerCase().includes(searchQuery.trim().toLowerCase()),
        )
      : [];

  const navigate = useNavigate();

  // Refs for the trigger buttons AND the portaled panels (both needed for
  // outside-click detection, since createPortal moves panel DOM nodes to
  // document.body — they are NOT descendants of the trigger wrapper divs).
  const profileRef = useRef(null);
  const profilePanelRef = useRef(null);
  const notifRef = useRef(null);
  const notifPanelRef = useRef(null);
  const imagePreviewRef = useRef(null);
  const searchResultsPanelRef = useRef(null);

  const goToSearchResult = (path) => {
    setShowSearchResults(false);
    navigate(path);
  };

  // ---- Fetch profile from backend ----
  // ✅ ADD — Redux hooks
  const {
    profileData,
    isUpdatingPassword,
    loadProfile,
    changePassword,
    uploadPicture,
    deletePicture,
    clearPasswordState: clearPwState,
  } = useProfile();

  const { logoutUser } = useAuth();

  // Load profile on mount
  useEffect(() => {
    loadProfile();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Load notifications from localStorage + auto-archive old ones ----
  const refreshNotifs = () => {
    const raw = loadNotifs(getLoggedInUserId());
    const processed = processNotifs(raw);
    saveNotifs(processed);
    setNotifications([...processed]);
  };

  useEffect(() => {
    refreshNotifs();
    // Listen for pushNotification events from other parts of the app
    window.addEventListener("qwickhub_notif_push", refreshNotifs);
    return () =>
      window.removeEventListener("qwickhub_notif_push", refreshNotifs);
  }, []);

  // ---- Poll backend for new notifications every 60s ----
  useEffect(() => {
    const fetchNotifs = async () => {
      try {
        const res = await authFetch("/api/notifications?limit=10");
        const json = await res.json();
        if (json?.data && Array.isArray(json.data)) {
          const userId = getLoggedInUserId();
          const local = loadNotifs(userId);
          const deletedIds = loadDeletedNotifIds(userId);
          const localIds = new Set(local.map((n) => n._id));
          const incoming = json.data
            .filter(
              (n) =>
                !localIds.has(n._id) && !deletedIds.has(String(n._id)),
            )
            .map((n) => ({
              _id: n._id,
              title: n.title,
              // Backend sends a details object, not a lines array
              lines: n.details
                ? Object.entries(n.details).map(
                    ([key, val]) => `${key}: ${val}`,
                  )
                : [],
              createdAt: n.createdAt
                ? new Date(n.createdAt).getTime()
                : Date.now(),
              archived: n.isArchived || false,
            }));
          if (incoming.length > 0) {
            const merged = processNotifs([...incoming, ...local]);
            saveNotifs(merged);
            setNotifications([...merged]);
          }
        }
      } catch {
        /* backend not available */
      }
    };
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 60000);
    return () => clearInterval(interval);
  }, []);

  // ---- Close on outside click ----
  // FIX: profile/notif panels are rendered via createPortal into
  // document.body, so they are NOT DOM descendants of profileRef /
  // notifRef. We must also check the panel refs, or any click inside
  // the open panel (e.g. the avatar photo) will incorrectly count as
  // "outside" and close the panel before its own onClick can fire.
  useEffect(() => {
  const handleClickOutside = (e) => {
      // If the click happened inside the image preview modal, ignore it —
      // don't treat it as "outside" the profile panel.
      if (imagePreviewRef.current && imagePreviewRef.current.contains(e.target)) {
        return;
      }

      const clickedOutsideProfile =
        profileRef.current &&
        !profileRef.current.contains(e.target) &&
        (!profilePanelRef.current ||
          !profilePanelRef.current.contains(e.target));
      if (clickedOutsideProfile) setShowProfile(false);

      const clickedOutsideNotif =
        notifRef.current &&
        !notifRef.current.contains(e.target) &&
        (!notifPanelRef.current || !notifPanelRef.current.contains(e.target));
      if (clickedOutsideNotif) setShowNotif(false);

      const clickedOutsideSearch =
        searchRef.current &&
        !searchRef.current.contains(e.target) &&
        (!searchResultsPanelRef.current ||
          !searchResultsPanelRef.current.contains(e.target));
      if (clickedOutsideSearch) setShowSearchResults(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ---- Derived lists ----
  const activeNotifs = notifications.filter((n) => !n.archived);
  const archivedNotifs = notifications.filter((n) => n.archived);
  const unreadCount = activeNotifs.length;
  const currentList = notifTab === "all" ? activeNotifs : archivedNotifs;
  const visibleList = currentList.slice(0, visibleCount);
  const hasMore = currentList.length > visibleCount;

  // ---- Dismiss single notif ----
  const dismissOne = async (id) => {
    const fromArchive = notifTab === "archive";

    // Optimistic local update
    setNotifications((prev) => {
      const updated = fromArchive
        ? prev.filter((n) => n._id !== id) // delete permanently
        : prev.map((n) => (n._id === id ? { ...n, archived: true } : n));
      saveNotifs(updated);
      return [...updated];
    });

    if (!isBackendNotifId(id)) return;

    try {
      if (fromArchive) {
        rememberDeletedNotifIds([id]);
        await authFetch(`/api/notifications/${id}`, { method: "DELETE" });
      } else {
        await authFetch(`/api/notifications/${id}/archive`, {
          method: "PATCH",
        });
      }
    } catch {
      /* keep local state even if backend call fails */
    }
  };

  // ---- Clear all ----
  const clearAll = async () => {
    const fromArchive = notifTab === "archive";
    const snapshot = notifications;
    const archivedIds = snapshot
      .filter((n) => n.archived)
      .map((n) => n._id)
      .filter(isBackendNotifId);

    // Optimistic local update
    setNotifications((prev) => {
      const updated = fromArchive
        ? prev.filter((n) => !n.archived) // permanently remove archived
        : prev.map((n) => (!n.archived ? { ...n, archived: true } : n));
      saveNotifs(updated);
      return [...updated];
    });
    setVisibleCount(5);

    try {
      if (fromArchive) {
        rememberDeletedNotifIds(archivedIds);
        await authFetch("/api/notifications/archived/clear-all", {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ids: archivedIds }),
        });
      } else {
        await authFetch("/api/notifications/clear-all", { method: "DELETE" });
      }
    } catch {
      /* keep local state even if backend call fails */
    }
  };

  const switchTab = (tab) => {
    setNotifTab(tab);
    setVisibleCount(5);
  };

  // ---- Password ----
  const handleUpdatePassword = async () => {
    setPasswordError("");
    setPasswordSuccess("");

    if (
      !passwordForm.currentPassword ||
      !passwordForm.newPassword ||
      !passwordForm.confirmPassword
    ) {
      setPasswordError("All fields are required.");
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      setPasswordError("New password must be at least 8 characters.");
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError("New passwords do not match.");
      return;
    }

    const result = await changePassword(
      passwordForm.currentPassword,
      passwordForm.newPassword,
    );

    // Check if rejected
    if (result.meta?.requestStatus === "rejected") {
      setPasswordError(result.payload || "Failed to update password.");
      return;
    }

    // Success
    setPasswordSuccess("Password updated successfully.");
    setPasswordForm({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setTimeout(() => {
      setShowChangePassword(false);
      setPasswordSuccess("");
      clearPwState();
    }, 1500);
  };

  const closePasswordModal = () => {
    setShowChangePassword(false);
    setPasswordForm({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setPasswordError("");
    setPasswordSuccess("");
  };

  const handleLogOut = () => {
    logoutUser(); // dispatches logout() from authSlice
    // authSlice logout() clears localStorage + Redux state
    // ProtectedRoute will redirect to "/" automatically
    navigate("/");
  };

  const formatJoined = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr)
      .toLocaleDateString("en-US", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
      .toUpperCase();
  };

  // ===================== RENDER =====================
  return (
    <>
      <div
        className="topbar"
        style={{
          position: "sticky",
          top: 0,
        }}
      >
        {/* LEFT — hamburger (mobile only) + search */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flex: 1,
            minWidth: 0,
          }}
        >
          {/* Hamburger: only shows on mobile via CSS */}
          {toggleSidebar && (
            <button
              onClick={toggleSidebar}
              aria-label="Toggle sidebar"
              style={{
                display: "none",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 6,
                borderRadius: 8,
                flexShrink: 0,
              }}
              className="mobile-menu-btn"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#444"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
          )}
          <div
            className="topbar-left "
            style={{ flex: 1, minWidth: 0, overflow: "visible" }}
          >
            <div
              ref={searchRef}
              className="search-box"
              style={{
                backgroundColor: "#ffffff",
                width: "100%",
                maxWidth: 560,
                minWidth: 0,
                height: 50,
                position: "relative",
                overflow: "visible",
              }}
            >
              <Search className="search-icon" size={24} />
              <input
                type="text"
                placeholder="Search for projects"
                value={searchQuery}
                style={{ fontSize: 16 }}
                onChange={(e) => {
                  const value = e.target.value;

                  setSearchQuery(value);
                  setShowSearchResults(value.trim().length > 0);
                  onSearch(value);
                }}
                onFocus={() => {
                  if (searchQuery.trim().length > 0) setShowSearchResults(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Escape") setShowSearchResults(false);
                }}
                aria-label="Search bar"
              />

              {/* ── SEARCH RESULTS DROPDOWN (portaled) ── */}
              {showSearchResults &&
                searchQuery.trim().length > 0 &&
                searchDropdownRect &&
                createPortal(
                  <div
                    ref={searchResultsPanelRef}
                    style={{
                      position: "fixed",
                      top: searchDropdownRect.bottom + 8,
                      left: searchDropdownRect.left,
                      width: searchDropdownRect.width,
                      background: "#fff",
                      borderRadius: 12,
                      boxShadow: "0 8px 32px rgba(0,0,0,0.14)",
                      zIndex: 2500,
                      maxHeight: "min(320px, 60vh)",
                      overflowY: "auto",
                      border: "1px solid #f0f0f0",
                    }}
                  >
                    {matchedProjects.length === 0 ? (
                      <div
                        style={{
                          padding: "18px 16px",
                          fontSize: 13,
                          color: "#9ca3af",
                          textAlign: "center",
                        }}
                      >
                        No results found
                      </div>
                    ) : (
                      matchedProjects.map((p) => (
                        <React.Fragment key={p._id}>
                          <div
                            onClick={() =>
                              goToSearchResult(`/dashboard/projects/${p._id}`)
                            }
                            className="hover:bg-[#F5F7FF]"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 12,
                              padding: "11px 16px",
                              cursor: "pointer",
                              borderBottom: "1px solid #f5f5f5",
                            }}
                          >
                            <span
                              style={{
                                fontSize: 13,
                                fontWeight: 500,
                                color: "#1a1a2e",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {p.name}
                            </span>
                            <span className="flex-shrink-0 text-[11px] font-semibold text-gray-300 bg-white px-[10px] py-[3px] rounded-full">
                              Project
                            </span>
                          </div>
                          <div
                            onClick={() =>
                              goToSearchResult(`/dashboard/kanban/${p._id}`)
                            }
                            className="hover:bg-[#F5F7FF]"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 12,
                              padding: "11px 16px",
                              cursor: "pointer",
                              borderBottom: "1px solid #f5f5f5",
                            }}
                          >
                            <span
                              style={{
                                fontSize: 13,
                                fontWeight: 500,
                                color: "#1a1a2e",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {p.name}
                            </span>
                            <span className="flex-shrink-0 text-[11px] font-semibold text-gray-300 bg-white px-[10px] py-[3px] rounded-full">
                              Kanban
                            </span>
                          </div>
                        </React.Fragment>
                      ))
                    )}
                  </div>,
                  document.body,
                )}
            </div>
          </div>
        </div>

        {/* RIGHT */}
        <div
          className="topbar-right"
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          {/* ===== BELL ===== */}
          <div ref={notifRef} style={{ position: "relative" }}>
            <button
              aria-label="Notifications"
              className="icon-btn pr-3"
              style={{ position: "relative" }}
              onClick={() => {
                setShowNotif((p) => !p);
                setShowProfile(false);
              }}
            >
              <Bell className="icon " size={22} />
              {unreadCount > 0 && (
                <span
                  style={{
                    position: "absolute",
                    top: 2,
                    right: 6,
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    background: "#e53935",
                    color: "#fff",
                    fontSize: 9,
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "2px solid #fff",
                    lineHeight: 1,
                  }}
                >
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </button>

            {/* ===== NOTIFICATION PANEL (portaled) ===== */}
            {showNotif &&
              notifRect &&
              createPortal(
                <div
                  ref={notifPanelRef}
                  style={{
                    position: "fixed",
                    top: notifRect.bottom + 8,
                    right: window.innerWidth - notifRect.right,
                    width: 420,
                    maxWidth: "95vw",
                    background: "#fff",
                    borderRadius: 16,
                    boxShadow: "0 8px 40px rgba(0,0,0,0.13)",
                    zIndex: 2500,
                    overflow: "hidden",
                    maxHeight: "80vh",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* Header */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "18px 20px 12px",
                      borderBottom: "1px solid #f0f0f0",
                      flexShrink: 0,
                    }}
                  >
                    <span
                      style={{ fontWeight: 700, fontSize: 18, color: "#1a1a2e" }}
                    >
                      Notifications
                    </span>
                    <button
                      onClick={clearAll}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        fontSize: 13,
                        color: "#3b6fd4",
                        fontWeight: 500,
                        padding: 0,
                      }}
                    >
                      Clear all
                    </button>
                  </div>

                  {/* Tabs */}
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      padding: "10px 20px 6px",
                      flexShrink: 0,
                    }}
                  >
                    {["all", "archive"].map((tab) => (
                      <button
                        key={tab}
                        onClick={() => switchTab(tab)}
                        style={{
                          padding: "4px 16px",
                          borderRadius: 20,
                          border: "none",
                          cursor: "pointer",
                          fontSize: 13,
                          fontWeight: 600,
                          background: notifTab === tab ? "#3b6fd4" : "#f0f4ff",
                          color: notifTab === tab ? "#fff" : "#555",
                          transition: "all 0.15s",
                          textTransform: "capitalize",
                        }}
                      >
                        {tab === "all" ? "All" : "Archive"}
                      </button>
                    ))}
                  </div>

                  {/* List */}
                  <div style={{ overflowY: "auto", flex: 1 }}>
                    {visibleList.length === 0 ? (
                      <div
                        style={{
                          textAlign: "center",
                          color: "#aaa",
                          fontSize: 13,
                          padding: "32px 20px",
                        }}
                      >
                        {notifTab === "all"
                          ? "No new notifications"
                          : "Archive is empty"}
                      </div>
                    ) : (
                      visibleList.map((notif) => (
                        <NotifItem
                          key={notif._id}
                          notif={notif}
                          onDismiss={() => dismissOne(notif._id)}
                        />
                      ))
                    )}

                    {/* Load more */}
                    {hasMore && (
                      <div
                        style={{ textAlign: "center", padding: "12px 0 16px" }}
                      >
                        <button
                          onClick={() => setVisibleCount((c) => c + 5)}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            fontSize: 13,
                            color: "#3b6fd4",
                            fontWeight: 500,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <RefreshCw size={13} />
                          Load more
                        </button>
                      </div>
                    )}
                    {!hasMore && visibleList.length > 0 && (
                      <div style={{ height: 12 }} />
                    )}
                  </div>
                </div>,
                document.body,
              )}
          </div>

          {/* ===== PROFILE ===== */}
          <div ref={profileRef} style={{ position: "relative" }}>
            <button
              aria-label="User profile"
              className="icon-btn"
              onClick={() => {
                setShowProfile((p) => !p);
                setShowNotif(false);
              }}
              style={{
                width: 35,
                height: 35,
                borderRadius: "50%",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
                background: profileData?.profilePicture
                  ? "transparent"
                  : "none",
                flexShrink: 0,
              }}
            >
              {profileData?.profilePicture ? (
                <img
                  src={profileData.profilePicture}
                  alt="Profile"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: "50%",
                  }}
                />
              ) : (
                <User className="profile" size={22} />
              )}
            </button>

            {/* ===== PROFILE PANEL (portaled) ===== */}
            {showProfile &&
              profileRect &&
              createPortal(
                <div
                  ref={profilePanelRef}
                  style={{
                    position: "fixed",
                    top: profileRect.bottom + 8,
                    right: window.innerWidth - profileRect.right,
                    width: "320px",
                    background: "#fff",
                    borderRadius: "16px",
                    boxShadow: "0 8px 32px rgba(0,0,0,0.13)",
                    zIndex: 2500,
                    overflow: "hidden",
                  }}
                >
                  {/* Blue banner */}
                  <div
                    style={{
                      background: "#CFDEFA47",
                      padding: "28px 20px 20px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      position: "relative",
                    }}
                  >
                    {/* ✅ Avatar with camera icon overlay — clicking opens file picker */}
                    <div style={{ position: "relative", marginBottom: 10 }}>
                      <input
                        type="file"
                        accept="image/*"
                        id="avatar-upload"
                        style={{ display: "none" }}
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const formData = new FormData();
                          formData.append("profilePic", file);
                          uploadPicture(formData);
                          // Redux thunk handles the upload + updates profileData.profilePicture
                          e.target.value = "";
                        }}
                      />
                      <div style={{ cursor: "pointer" }}>
                        <div
                          onClick={() =>
                            profileData?.profilePicture &&
                            setShowImagePreview(true)
                          }
                          style={{
                            width: 72,
                            height: 72,
                            borderRadius: "50%",
                            background: "#c7d9f5",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 36,
                            overflow: "hidden",
                            border: "3px solid #fff",
                          }}
                        >
                          {profileData?.profilePicture ? (
                            <img
                              src={profileData.profilePicture}
                              alt="avatar"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <User size={36} color="#5b8dee" />
                          )}
                        </div>

                        {/* ✅ Camera icon badge — bottom-right of avatar */}
                        <label
                          htmlFor="avatar-upload"
                          style={{
                            position: "absolute",
                            bottom: 0,
                            right: 0,
                            width: 22,
                            height: 22,
                            borderRadius: "50%",
                            background: "#3b6fd4",
                            border: "2px solid #fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                          }}
                        >
                          {/* Camera SVG */}
                          <svg
                            width="11"
                            height="11"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#fff"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                            <circle cx="12" cy="13" r="4" />
                          </svg>
                        </label>
                      </div>
                    </div>

                    <p
                      style={{
                        fontWeight: 700,
                        fontSize: 18,
                        margin: 0,
                        color: "#1a1a2e",
                      }}
                    >
                      {profileData?.name || "User"}
                    </p>
                    <p
                      style={{ fontSize: 13, color: "#555", margin: "2px 0 8px" }}
                    >
                      {profileData?.email || ""}
                    </p>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: "center",
                        gap: "8px",
                        marginTop: "4px",
                      }}
                    >
                      {(profileData?.specialization || []).map((s) => (
                        <span
                          key={s}
                          style={{
                            background: "#E0EAFF",
                            color: "#3B6FD4",
                            fontSize: "11px",
                            fontWeight: 600,
                            padding: "6px 16px",
                            borderRadius: "182px",
                            letterSpacing: "0.5px",
                            textTransform: "uppercase",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Info */}
                  <div style={{ padding: "16px 20px 0" }}>
                    <InfoRow
                      icon={<IdCard size={17} color="#5b8dee" />}
                      label="Employee ID"
                      value={profileData?.employeeId || "—"}
                    />
                    <InfoRow
                      icon={<Calendar size={17} color="#5b8dee" />}
                      label="Joined"
                      value={formatJoined(profileData?.dateOfJoining) || "—"}
                    />
                    <InfoRow
                      icon={<UserCircle size={17} color="#5b8dee" />}
                      label="Role"
                      value={profileData?.role || "—"}
                      uppercase
                    />
                  </div>

                  <div
                    style={{ height: 1, background: "#f0f0f0", margin: "0 20px" }}
                  />

                  <button
                    onClick={() => {
                      setShowProfile(false);
                      setShowChangePassword(true);
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      padding: "14px 20px",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      fontSize: 14,
                      color: "#1a1a2e",
                      fontWeight: 500,
                    }}
                  >
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 10 }}
                    >
                      <Lock size={16} color="#555" />
                      Change Password
                    </div>
                    <ChevronRight size={16} color="#aaa" />
                  </button>

                  <div
                    style={{ height: 1, background: "#f0f0f0", margin: "0 20px" }}
                  />

                  <button
                    onClick={handleLogOut}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      width: "100%",
                      padding: "14px 20px",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      fontSize: 14,
                      color: "#e53935",
                      fontWeight: 500,
                      marginBottom: 4,
                    }}
                  >
                    <LogOut size={16} color="#e53935" />
                    Log Out
                  </button>
                </div>,
                document.body,
              )}
          </div>
        </div>
      </div>

{showImagePreview && profileData?.profilePicture && (
  <div
    ref={imagePreviewRef}
    style={{
      position: "fixed",
      inset: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 9999,
      background: "rgba(0,0,0,0.66)",
      backdropFilter: "blur(5px)",
    }}
    onClick={() => setShowImagePreview(false)}
  >
    <button
      onClick={() => setShowImagePreview(false)}
      style={{
        position: "absolute",
        top: 24,
        right: 24,
        background: "none",
        border: "none",
        color: "#fff",
        fontSize: 32,
        cursor: "pointer",
        lineHeight: 1,
      }}
    >
      ×
    </button>

   <div
  style={{
    position: "relative",
    width: 530,
    height: 530,
    maxWidth: "90vw",
    maxHeight: "90vh",
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
  }}
  onClick={(e) => e.stopPropagation()}
>
  <img
    src={profileData.profilePicture}
    alt="Profile"
    style={{
      width: "100%",
      height: "100%",
      objectFit: "cover",
      display: "block",
    }}
  />

  <div
    onClick={handleDeletePicture}
    role="button"
    aria-label="Delete profile picture"
    style={{
      position: "absolute",
      left: 0,
      bottom: 0,
      width: "100%",
      height: 56,
      background: "#000000",
      opacity: isDeletingPicture ? 0.35 : 0.52,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: isDeletingPicture ? "not-allowed" : "pointer",
      pointerEvents: isDeletingPicture ? "none" : "auto",
    }}
  >
    <Trash2 size={30} color="#FF0000" strokeWidth={2} />
  </div>
</div>
  </div>
)}

      {/* ===== CHANGE PASSWORD MODAL ===== */}
      {showChangePassword && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.25)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              padding: "36px 36px 28px",
              width: "100%",
              maxWidth: 460,
              position: "relative",
              boxShadow: "0 8px 40px rgba(0,0,0,0.15)",
            }}
          >
            <button
              onClick={closePasswordModal}
              style={{
                position: "absolute",
                top: 16,
                right: 18,
                background: "none",
                border: "none",
                fontSize: 20,
                cursor: "pointer",
                color: "#888",
                lineHeight: 1,
              }}
            >
              ×
            </button>

            <h2
              style={{
                fontSize: 20,
                fontWeight: 700,
                margin: "0 0 6px",
                color: "#1a1a2e",
              }}
            >
              Change Password
            </h2>
            <p style={{ fontSize: 13, color: "#888", margin: "0 0 24px" }}>
              Enter your current password and choose a new password
            </p>

            <PwField
              label="Current Password"
              value={passwordForm.currentPassword}
              show={showCurrent}
              onToggle={() => setShowCurrent((p) => !p)}
              placeholder="Enter your current password"
              mb={18}
              onChange={(v) =>
                setPasswordForm({ ...passwordForm, currentPassword: v })
              }
            />
            <PwField
              label="New Password"
              value={passwordForm.newPassword}
              show={showNew}
              onToggle={() => setShowNew((p) => !p)}
              placeholder="Enter your new password"
              mb={6}
              hint="Use 8 or more characters with a mix of letters, numbers & symbols."
              onChange={(v) =>
                setPasswordForm({ ...passwordForm, newPassword: v })
              }
            />
            <PwField
              label="Confirm New Password"
              value={passwordForm.confirmPassword}
              show={showConfirm}
              onToggle={() => setShowConfirm((p) => !p)}
              placeholder="Enter your new password"
              mb={20}
              onChange={(v) =>
                setPasswordForm({ ...passwordForm, confirmPassword: v })
              }
            />

            {passwordError && (
              <p
                style={{
                  fontSize: 12,
                  color: "#e53935",
                  margin: "-8px 0 12px",
                }}
              >
                {passwordError}
              </p>
            )}
            {passwordSuccess && (
              <p
                style={{
                  fontSize: 12,
                  color: "#43a047",
                  margin: "-8px 0 12px",
                }}
              >
                {passwordSuccess}
              </p>
            )}

            <div
              style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}
            >
              <button
                onClick={closePasswordModal}
                style={{
                  padding: "10px 28px",
                  border: "1.5px solid #3b6fd4",
                  borderRadius: 8,
                  background: "#fff",
                  color: "#3b6fd4",
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdatePassword}
                disabled={isUpdatingPassword}
                style={{
                  padding: "10px 28px",
                  border: "none",
                  borderRadius: 8,
                  background: "#3b6fd4",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: isUpdatingPassword ? "not-allowed" : "pointer",
                  opacity: isUpdatingPassword ? 0.7 : 1,
                }}
              >
                {isUpdatingPassword ? "Updating..." : "Update"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

Topbar.propTypes = {
  onSearch: PropTypes.func,
  onNotification: PropTypes.func,
  toggleSidebar: PropTypes.func,
};

export default Topbar;

export const pushNotification = ({ title, lines = [] }) => {
  const userId = getLoggedInUserId();
  const key = getNotifKey(userId);
  const raw = (() => {
    try {
      return JSON.parse(localStorage.getItem(key)) || [];
    } catch {
      return [];
    }
  })();
  const newNotif = {
    _id: `local_${Date.now()}_${Math.random().toString(36).slice(2)}`,
    title,
    lines,
    createdAt: Date.now(),
    archived: false,
  };
  localStorage.setItem(key, JSON.stringify([newNotif, ...raw]));
  window.dispatchEvent(new CustomEvent("qwickhub_notif_push"));
};